import { produce } from "immer";
import pluralize from "pluralize";
import { useHistory } from "react-router-dom";
import { showNotification } from "@mantine/notifications";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  createTickets,
  CreateTicketsRequest,
  CreateTicketsResponse,
} from "@shared/api/lib/ticketing/createTicket";
import { Finding, FindingsFilters } from "@shared/types";

import { useOrg } from "../useOrg";

const showNotitications = ({
  succeeded,
  failed,
  skipped,
}: CreateTicketsResponse) => {
  if (failed.length > 0) {
    if (succeeded.length > 0) {
      showNotification({
        title: "Some tickets were not created",
        message: `There was a problem creating ${failed.length} of ${
          failed.length + succeeded.length
        } tickets in your request. Please try again.`,
        color: "yellow",
      });
    } else {
      showNotification({
        title: "Ticket not created",
        message:
          failed.length > 1
            ? "There was a problem creating tickets for these findings. Please try again."
            : "There was a problem creating a ticket for this finding. Please try again.",
        color: "red",
      });
    }
    failed.forEach((failure) => console.warn(failure.error));
  }
  if (succeeded.length === 1) {
    showNotification({
      title: "Ticket created for finding",
      message: `${succeeded[0].external_slug} was created. Click here to view it.`,
      color: "green",
      onClick: () => {
        window.open(succeeded[0].ticket_url, "_blank");
      },
    });
  } else if (succeeded.length > 0) {
    showNotification({
      title: "Tickets created for findings",
      message: `${succeeded.length} tickets were created.`,
      color: "green",
    });
  }
  if (skipped.length) {
    showNotification({
      title: "Tickets skipped",
      message:
        skipped.length > 1
          ? `${skipped.length} tickets were skipped because they already exist.`
          : "1 ticket was skipped because it already exists.",
      color: "yellow",
    });
  }
};

export const useCreateTicketMutation = () => {
  const history = useHistory();
  const [org] = useOrg();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      variables: { filterOverrides?: FindingsFilters } & CreateTicketsRequest
    ) => {
      const searchParams = new URLSearchParams(history.location.search);
      if (variables.filterOverrides) {
        Object.entries(variables.filterOverrides).forEach(([key, value]) => {
          if (value) {
            searchParams.set(key, value);
          }
        });
      }
      // Handle the case where the user selected rule ids and specific findings
      const issueIdsLength = variables.issueIds?.length || 0;
      const hasRuleId = variables.filterOverrides?.rule_id;
      const promises: Promise<CreateTicketsResponse>[] = [];
      if (hasRuleId) {
        promises.push(
          createTickets({
            deploymentId: variables.deploymentId,
            ticketingId: variables.ticketingId,
            searchParams: searchParams,
          })
        );
        if (issueIdsLength > 0) {
          searchParams.delete("rule_id");
        }
      }
      if (issueIdsLength > 0) {
        promises.push(createTickets({ ...variables, searchParams }));
      }
      return Promise.all(promises);
    },
    onSuccess: (responses, variables) => {
      responses.forEach(({ succeeded, failed, skipped }) => {
        showNotitications({ succeeded, failed, skipped });
        queryClient.invalidateQueries({
          queryKey: ["DeploymentFindings", org?.id],
        });
        succeeded.forEach((ticket) => {
          queryClient.setQueryData(
            ["finding", { org, id: ticket.issue_id.toString() }],
            (old) =>
              produce(old as Finding | undefined, (draft) => {
                if (draft) {
                  draft.external_ticket = {
                    ticketing_instance_id: Number.parseInt(
                      variables.ticketingId,
                      10
                    ),
                    id: ticket.ticket_id,
                    url: ticket.ticket_url,
                    issue_id: ticket.issue_id,
                    external_slug: ticket.external_slug,
                  };
                }
              })
          );
        });
      });
    },
    onError: (err, variables) => {
      showNotification({
        title: `${pluralize("Ticket", variables.issueIds?.length)} not created`,
        message: `Error creating ${pluralize(
          "ticket",
          variables.issueIds?.length
        )}: ${err}`,
      });
    },
  });
};
